import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Divider,
  Stack,
  TextField,
  MenuItem,
  Button,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AxiosInstance from "service/axiosInstance";
import { useToast } from "hooks";
import { useGetPaymentSource, useGetAllTransactionTypes } from "pages/Dashboard/dashboardQueries";
import { useGetActiveDevices } from "pages/ManageDevices/deviceQueries";

const modeOfPayment = [
  "Cash",
  "Debit Card",
  "UPI (Google pay/Phone Pe)",
  "Mobile Wallets",
  "Credit (at the shop)",
  "Credit Card",
  "Bank Transfer",
  "Net Banking",
  "Any other",
];

function formatDateForInput(dateValue) {
  if (!dateValue) return "";
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function EditTransactionModal({ isOpen, onClose, transaction }) {
  const queryClient = useQueryClient();
  const responseToast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const { data: activeDevicesData } = useGetActiveDevices();
  const activeDevicesList = activeDevicesData?.data ?? [];

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      transactionDate: formatDateForInput(transaction?.transactionDate),
      amount: transaction?.amount ?? "",
      type: transaction?.type ?? "",
      source: transaction?.paymentSourceId ? String(transaction.paymentSourceId) : "",
      mode: transaction?.mode ?? "",
      description: transaction?.description ? transaction.description.split(", ").filter(Boolean) : [],
      descriptionOther: "",
      purpose: transaction?.purpose ?? "",
      deviceId: transaction?.deviceId ?? "",
      interest: transaction?.interest ? "yes" : "no",
    },
  });

  const watchType = watch("type");
  const watchSource = watch("source");
  const watchDescription = watch("description");

  const sourceId = Number(watchSource);

  const { data: paymentSourceData } = useGetPaymentSource(
    watchType || transaction?.type
  );
  const { data: txTypeData } = useGetAllTransactionTypes();
  const txTypes = txTypeData?.data?.filter((t) => t.isActive) ?? [];

  const selectedSource = paymentSourceData?.data?.find(
    (d) => d.id === sourceId
  );

  // Pre-fill form when modal opens or transaction changes
  useEffect(() => {
    if (transaction && isOpen) {
      reset({
        transactionDate: formatDateForInput(transaction.transactionDate),
        amount: transaction.amount ?? "",
        type: transaction.type ?? "",
        source: transaction.paymentSourceId ? String(transaction.paymentSourceId) : "",
        mode: transaction.mode ?? "",
        description: transaction.description ? transaction.description.split(", ").filter(Boolean) : [],
        descriptionOther: "",
        purpose: transaction.purpose ?? "",
        deviceId: transaction.deviceId ?? "",
        interest: transaction.interest ? "yes" : "no",
      });
    }
  }, [transaction, isOpen, reset]);

  // Re-set source once payment source data loads (handles async timing)
  useEffect(() => {
    if (paymentSourceData?.data?.length && transaction?.paymentSourceId) {
      setValue("source", String(transaction.paymentSourceId), { shouldDirty: false });
    }
  }, [paymentSourceData, transaction?.paymentSourceId, setValue]);

  // Re-set description once selectedSource categories are available
  useEffect(() => {
    if (!selectedSource?.category?.length || !transaction?.description) return;
    const categoryNames = selectedSource.category.map((c) => c.name ?? c);
    const splitDescriptions = transaction.description.split(", ").filter(Boolean);
    
    // Check which ones are in categories vs others
    const validCategories = splitDescriptions.filter((d) => categoryNames.includes(d));
    const otherDescriptions = splitDescriptions.filter((d) => !categoryNames.includes(d));
    
    if (otherDescriptions.length > 0) {
      setValue("description", [...validCategories, "Others"], { shouldDirty: false });
      setValue("descriptionOther", otherDescriptions.join(", "), { shouldDirty: false });
    } else {
      setValue("description", validCategories, { shouldDirty: false });
      setValue("descriptionOther", "", { shouldDirty: false });
    }
  }, [selectedSource?.category, transaction?.description, setValue]);

  const onSubmit = async (data) => {
    setSubmitting(true);
    setApiError("");
    // If "Others" selected, use the free-text value as description
    let finalDescription = "";
    if (Array.isArray(data.description)) {
      const parts = data.description.filter((d) => d !== "Others");
      if (data.description.includes("Others") && data.descriptionOther) {
        parts.push(data.descriptionOther);
      }
      finalDescription = parts.join(", ");
    } else {
      finalDescription = data.description === "Others" ? data.descriptionOther : data.description;
    }

    try {
      await AxiosInstance({
        method: "PATCH",
        url: `api/v1/transaction/${transaction.id}`,
        data: {
          transactionDate: new Date(data.transactionDate).toISOString(),
          amount: Number(data.amount),
          type: data.type,
          source: data.source ? Number(data.source) : undefined,
          mode: data.mode,
          description: finalDescription,
          purpose: data.purpose || undefined,
          deviceId: data.deviceId || undefined,
          interest: data.interest === "yes",
        },
      });
      queryClient.invalidateQueries(["transactions-paginated-data"]);
      responseToast.toastSuccess("Transaction updated successfully");
      onClose();
    } catch (err) {
      const message =
        err?.message || err?.error || "An error occurred. Please try again.";
      setApiError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const categoryOptions = selectedSource?.category ?? [];

  const showDescriptionDropdown =
    watchType !== "NONE" && (categoryOptions.length > 0 || (watchSource && !selectedSource));
  const showOtherTextBox =
    showDescriptionDropdown &&
    (Array.isArray(watchDescription)
      ? watchDescription.includes("Others")
      : watchDescription === "Others");

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        component="div"
        className="flex justify-between items-center"
      >
        <Typography variant="h6" component="span" className="font-semibold">
          Edit Transaction
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3} className="py-2">
            {/* Transaction Date */}
            <Box className="grid grid-cols-2 items-center gap-4">
              <label className="text-[#1B2559] text-[14px] font-[700]">
                Transaction Date <span className="text-red-500">*</span>
              </label>
              <div>
                <input
                  {...register("transactionDate", {
                    required: "Transaction Date is required.",
                    validate: (v) =>
                      !isNaN(new Date(v).getTime()) ||
                      "Transaction Date must be a valid date.",
                  })}
                  type="date"
                  data-testid="field-transactionDate"
                  className="rounded-lg px-3 h-[50px] border border-[#1B2559] w-full"
                />
                {errors.transactionDate && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.transactionDate.message}
                  </p>
                )}
              </div>
            </Box>

            {/* Device ID (Optional) */}
            <Box className="grid grid-cols-2 items-center gap-4">
              <label className="text-[#1B2559] text-[14px] font-[700]">
                Device ID (Optional)
              </label>
              <div>
                <Controller
                  name="deviceId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "50px",
                          borderRadius: "10px",
                        },
                      }}
                      fullWidth
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {activeDevicesList.map((dev) => (
                        <MenuItem key={dev.id} value={dev.deviceId}>
                          {dev.name} ({dev.deviceId})
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </div>
            </Box>
            {/* Interest */}
            <Box className="grid grid-cols-2 items-center gap-4">
              <label className="text-[#1B2559] text-[14px] font-[700]">
                Interest
              </label>
              <div>
                <Controller
                  name="interest"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "50px",
                          borderRadius: "10px",
                        },
                      }}
                      fullWidth
                    >
                      <MenuItem value="no">No</MenuItem>
                      <MenuItem value="yes">Yes</MenuItem>
                    </TextField>
                  )}
                />
              </div>
            </Box>

            {/* Amount */}
            <Box className="grid grid-cols-2 items-center gap-4">
              <label className="text-[#1B2559] text-[14px] font-[700]">
                Amount <span className="text-red-500">*</span>
              </label>
              <div>
                <TextField
                  {...register("amount", {
                    required: "Amount is required.",
                    validate: (v) =>
                      Number(v) > 0 || "Amount must be a positive number.",
                  })}
                  type="number"
                  inputProps={{
                    min: 0.01,
                    step: "any",
                    "data-testid": "field-amount",
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "50px",
                      borderRadius: "10px",
                    },
                  }}
                  fullWidth
                  error={Boolean(errors.amount)}
                  helperText={errors.amount?.message}
                />
              </div>
            </Box>

            {/* Type / Category */}
            <Box className="grid grid-cols-2 items-center gap-4">
              <label className="text-[#1B2559] text-[14px] font-[700]">
                Type <span className="text-red-500">*</span>
              </label>
              <div>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: "Type is required." }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "50px",
                          borderRadius: "10px",
                        },
                      }}
                      fullWidth
                      error={Boolean(errors.type)}
                      helperText={errors.type?.message}
                      inputProps={{ "data-testid": "field-type" }}
                    >
                      {txTypes.length > 0 ? (
                        txTypes.map((t) => (
                          <MenuItem key={t.value} value={t.value}>
                            {t.label}
                          </MenuItem>
                        ))
                      ) : (
                        <>
                          <MenuItem value="CREDIT">INFLOW</MenuItem>
                          <MenuItem value="DEBIT">OUTFLOW</MenuItem>
                          <MenuItem value="NONE">NO TRANSACTION</MenuItem>
                        </>
                      )}
                    </TextField>
                  )}
                />
              </div>
            </Box>

            {/* Sub-category (PaymentSource) */}
            {watchType !== "NONE" && (
              <Box className="grid grid-cols-2 items-center gap-4">
                <label className="text-[#1B2559] text-[14px] font-[700]">
                  Category <span className="text-red-500">*</span>
                </label>
                <div>
                  <Controller
                    name="source"
                    control={control}
                    rules={{
                      required:
                        watchType !== "NONE"
                          ? "Category is required."
                          : false,
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "50px",
                            borderRadius: "10px",
                          },
                        }}
                        fullWidth
                        onChange={(e) => {
                          field.onChange(e);
                          setValue("description", []);
                        }}
                        error={Boolean(errors.source)}
                        helperText={errors.source?.message}
                        inputProps={{ "data-testid": "field-source" }}
                      >
                        {paymentSourceData?.data?.map((option) => (
                          <MenuItem key={option.id} value={String(option.id)}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </div>
              </Box>
            )}

            {/* Mode of Payment */}
            <Box className="grid grid-cols-2 items-center gap-4">
              <label className="text-[#1B2559] text-[14px] font-[700]">
                Mode of Payment <span className="text-red-500">*</span>
              </label>
              <div>
                <Controller
                  name="mode"
                  control={control}
                  rules={{ required: "Mode of payment is required." }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "50px",
                          borderRadius: "10px",
                        },
                      }}
                      fullWidth
                      error={Boolean(errors.mode)}
                      helperText={errors.mode?.message}
                      inputProps={{ "data-testid": "field-mode" }}
                    >
                      {modeOfPayment.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </div>
            </Box>

            {/* Description */}
            <Box className="grid grid-cols-2 items-center gap-4">
              <label className="text-[#1B2559] text-[14px] font-[700]">
                Sub-category
              </label>
              <Stack spacing={1}>
                {showDescriptionDropdown ? (
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        disabled={categoryOptions.length === 0}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "50px",
                            borderRadius: "10px",
                          },
                        }}
                        fullWidth
                        SelectProps={{
                          multiple: true,
                          renderValue: (selected) => (Array.isArray(selected) ? selected.join(", ") : selected),
                        }}
                        inputProps={{ "data-testid": "field-description" }}
                      >
                        {categoryOptions.map((option) => (
                          <MenuItem
                            key={option.id ?? option}
                            value={option.name ?? option}
                          >
                            {option.name ?? option}
                          </MenuItem>
                        ))}
                        <MenuItem value="Others">Others</MenuItem>
                      </TextField>
                    )}
                  />
                ) : (
                  <TextField
                    {...register("description")}
                    inputProps={{ "data-testid": "field-description" }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    fullWidth
                    multiline
                    rows={2}
                  />
                )}

                {/* Free-text box when "Others" is selected */}
                {showOtherTextBox && (
                  <TextField
                    {...register("descriptionOther", {
                      required: "Please specify description.",
                    })}
                    placeholder="Please specify..."
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    fullWidth
                    error={Boolean(errors.descriptionOther)}
                    helperText={errors.descriptionOther?.message}
                  />
                )}
              </Stack>
            </Box>

            {/* Purpose */}
            <Box className="grid grid-cols-2 items-center gap-4">
              <label className="text-[#1B2559] text-[14px] font-[700]">
                Purpose
              </label>
              <div>
                <TextField
                  {...register("purpose")}
                  inputProps={{ "data-testid": "field-purpose" }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  fullWidth
                  multiline
                  rows={2}
                  error={Boolean(errors.purpose)}
                  helperText={errors.purpose?.message}
                />
              </div>
            </Box>

            {/* API error */}
            {apiError && (
              <p data-testid="error-message" className="text-red-500 text-sm">
                {apiError}
              </p>
            )}

            <Box className="flex justify-end gap-3 pt-2">
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={submitting}
                data-testid="submit-button"
              >
                {submitting ? "Saving..." : "Save"}
              </Button>
            </Box>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
}
